<template>
  <sticky-top-bar parent-container-selector="#products"
                  :offset="filtersOffset"
                  :active="!mobile"
                  :z-index="16"
                  v-resize="handleResize">
    <div class="products-listing-header">
      <div class="products-listing-header__top">
        <div class="products-listing-header__left">
          <span v-if="category.name && isTitleVisible" class="products-listing-header__category-name">{{ category.name }}</span>
          <products-listing-results-count class="products-listing-header__results-count" />
        </div>
        <div class="products-listing-header__right">
          <div class="products-listing-header__filters-container">
            <ais-dynamic-widgets>
              <ais-toggle-refinement v-for="filter in toggleFilters"
                                    :key="filter.value"
                                    :attribute="filter.value">
                <template v-slot="{ value, refine }">
                  <sn-toggle :value="value.isRefined"
                            :label="getToggleFilterName(filter)"
                            :id="filter.value"
                            @input="refine(value)"/>
                </template>
              </ais-toggle-refinement>
            </ais-dynamic-widgets>
          </div>

          <ais-sort-by :items="sortOptions" role="button">
            <template v-slot="{ items, currentRefinement, refine }">
              <div class="products-listing-header__multiselect-wrapper">
                SORT BY
                <multiselect :value="selectedSortOption(items, currentRefinement)"
                            :searchable="false"
                            :allow-empty="false"
                            :close-on-select="true"
                            :show-labels="false"
                            :options="items"
                            label="label"
                            class="sort-select"
                            track-by="value"
                            @input="refine($event.value)">
                  <div slot="caret"
                      slot-scope="{ toggle }"
                      class="caret"
                      role="button">
                    <i class="fas fa-angle-down" />
                    <i class="fas fa-angle-up" @click="toggle" />
                  </div>
                </multiselect>
              </div>
            </template>
          </ais-sort-by>
          <products-listing-pagination />
        </div>
      </div>

      <div class="products-listing-header__bottom">
        <products-listing-applied-filters :category="categoryPresence"
                                          :categories="categories" />
      </div>
    </div>
  </sticky-top-bar>
</template>

<script>
  import Multiselect from 'vue-multiselect'
  import { AisDynamicWidgets } from 'vue-instantsearch'
  import ProductsListingAppliedFilters from '@/components/products/listing/filters/ProductsListingAppliedFilters'
  import ProductsListingPagination from '@/components/products/listing/ProductsListingPagination'
  import ProductsListingResultsCount from '@/components/products/listing/ProductsListingResultsCount'
  import SnToggle from '@/components/SnToggle'
  import StickyTopBar from '@/../shared/components/StickyTopBar'

  import ProductsListingResponsive from '@/mixins/products/listing/productsListingResponsive'
  import ProductsListingSortMixin from '@/mixins/products/listing/productsListingSort'
  import ProductsListingToggleFiltersMixin from '@/mixins/products/listing/productsListingToggleFilters'
  import StickyFiltersMixin from '@/mixins/stickyFilters'

  import { isEmpty } from 'lodash'

  export default {
    name: 'ProductsListingHeader',
    components: {
      AisDynamicWidgets,
      Multiselect,
      ProductsListingAppliedFilters,
      ProductsListingPagination,
      ProductsListingResultsCount,
      SnToggle,
      StickyTopBar
    },
    mixins: [
      ProductsListingResponsive,
      ProductsListingSortMixin,
      ProductsListingToggleFiltersMixin,
      StickyFiltersMixin
    ],
    props: {
      category: {
        type: Object,
        default: () => ({})
      },
      categories: {
        type: Array,
        default: () => []
      },
      isTitleVisible: {
        type: Boolean,
        default: true
      }
    },
    data () {
      return {
        marginTop: 0
      }
    },
    computed: {
      categoryPresence () {
        return isEmpty(this.category) ? null : this.category
      }
    },
    methods: {
      selectedSortOption(options, selectedValue) {
        return options.find(option => option.value === selectedValue)
      }
    }
  }
</script>

<style scoped lang="scss">
  @import '@/assets/stylesheets/variables.scss';

  .products-listing-header {
    margin-bottom: 24px;
    padding-top: 10px;

    &__top {
      display: grid;
      grid-template-columns: minmax(314px, auto) 1fr;
      justify-content: space-between;
      align-items: baseline;
      padding-bottom: 24px;
    }

    &__bottom {
      border-top: 1px solid $border-line;
      padding-top: 16px;
      margin-bottom: -16px;
    }

    &__left {
      display: flex;
      align-items: baseline;
      column-gap: 40px;
      justify-content: space-between;
    }

    &__right, ::v-deep &__filters-container .ais-DynamicWidgets {
      display: flex;
      align-items: center;
      column-gap: 40px;
      text-align: right;
      justify-content: flex-end;
    }

    &__category-name {
      font-family: Tenez;
      font-size: 2.7rem;
      color: $gunmetal;
    }

    &__multiselect-wrapper {
      display: flex;
      align-items: center;
      column-gap: 24px;
      font-family: 'MotivaSans Light';
      font-size: 1.5rem;
      white-space: nowrap;
      color: $gunmetal;
      text-transform: uppercase;
    }

    /deep/ .multiselect {
      .caret {
        position: absolute;
        top: 0;
        right: 0;
        height: 40px;
        padding: 0 20px;
        display: flex;
        justify-content: center;
        align-items: center;
        cursor: pointer;
        font-size: 1.8rem;
        color: $color-black-dark;

        .fa-angle-up {
          display: none;

          &::before {
            content: '\f0d8';
          }
        }

        .fa-angle-down {
          &::before {
            content: '\f0d7';
          }
        }
      }
      &.multiselect--active {
        .caret {
          .fa-angle-up {
            display: block;

          }
          .fa-angle-down {
            display: none;
          }
        }
      }

      .multiselect__tags {
        width: 210px;
        border: 1px solid $border-line;
        border-radius: 5px;
        color: $color-black-dark;
        padding-top: 0;
        padding-bottom: 0;
        cursor: pointer;
        padding-right: 50px;
        min-height: 38px;
        display: flex;

        .multiselect__single {
          margin: 0;
          line-height: 30px;
          padding-left: 12px;
          font-size: 1.4rem;
          display: flex;
          align-items: center;
        }
      }

      .multiselect__content-wrapper {
        border: 1px solid $border-line;

        .multiselect__element {
          font-size: 1.4rem;
          padding: 0;

          .multiselect__option {
            padding-left: 20px;
            padding-right: 20px;
            font-family: 'MotivaSans Light';
            text-transform: uppercase;
          }
        }
      }
    }

    @media screen and (max-width: 1330px) {
      grid-template-columns: 270px 1fr;
      grid-column-gap: 40px;

      &__left, &__right, ::v-deep &__filters-container .ais-DynamicWidgets {
        column-gap: 20px;
      }
    }

    @media screen and (max-width: $products-filters-mobile-max) {
      border-bottom: none;
      padding: 0;

      &__results-count,
      &__right,
      &__bottom {
        display: none;
      }
    }
  }
</style>
